// MCHD Room Scheduler - Reports & Analytics Page
// Comprehensive reports and analytics dashboard for administrators

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Grid,
  Group,
  Button,
  Tabs,
  Paper,
  Badge,
  Progress,
  Alert,
  Loader,
  Center,
  Select,
  Modal,
  Box,
} from '@mantine/core';
import {
  IconChartBar,
  IconDownload,
  IconUsers,
  IconBuilding,
  IconClock,
  IconTrendingUp,
  IconFileText,
  IconAlertCircle,
  IconFileTypePdf,
  IconFileSpreadsheet,
  IconFileTypeCsv,
} from '@tabler/icons-react';
import { useReports } from '../../../hooks/admin/useReports';
import DynamicRefreshButton from '../../../components/atoms/DynamicRefreshButton';
import { notifications } from '@mantine/notifications';

interface ReportsAnalyticsProps {}

/**
 * Reports & Analytics page for administrators
 * Provides comprehensive system reports, utilization analytics, and performance metrics
 * @param props - Component props
 * @returns JSX element
 */
export function ReportsAnalytics({}: ReportsAnalyticsProps): JSX.Element {
  // State management
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [selectedReport, setSelectedReport] = useState<string>('utilization');
  const [showExportModal, setShowExportModal] = useState(false);

  // Use real reports hook
  const {
    analytics,
    analyticsLoading,
    analyticsError,
    refetchAnalytics,
    exportReport: exportReportData,
    // isExporting, // Unused for now
  } = useReports();

  // Handlers
  const handleGenerateReport = async (reportType: string) => {
    try {
      // For now, just show a notification since we don't have a generate endpoint
      notifications.show({
        title: 'Report Generated',
        message: `${reportType} report data is available in the analytics overview.`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate report. Please try again.',
        color: 'red',
      });
    }
  };

  const handleExportReport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      await exportReportData({
        type: selectedReport,
        format,
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: new Date().toISOString(),
      });
      setShowExportModal(false);
    } catch (error) {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export report. Please try again.',
        color: 'red',
      });
    }
  };

  const handleRefresh = () => {
    refetchAnalytics();
  };

  // Calculate key metrics
  const totalUtilization = analytics?.utilization?.overall || 0;
  const totalUsers = analytics?.users?.total || 0;
  const activeUsers = analytics?.users?.active || 0;
  const totalRooms = analytics?.rooms?.total || 0;
  const activeRooms = analytics?.rooms?.active || 0;
  const totalSessions = analytics?.sessions?.total || 0;
  const completedSessions = analytics?.sessions?.completed || 0;

  if (analyticsError) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Reports"
          color="red"
          variant="light"
        >
          Failed to load reports and analytics data
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Box>
            <Title order={1} mb="xs">
              Reports & Analytics
            </Title>
            <Text size="lg" c="dimmed">
              Comprehensive system reports, utilization analytics, and performance metrics
            </Text>
          </Box>
          <Group gap="sm">
            <DynamicRefreshButton
              variant="outline"
              onRefresh={handleRefresh}
              isLoading={analyticsLoading}
              size="sm"
              color="blue"
            />
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={() => setShowExportModal(true)}
              size="sm"
            >
              Export Report
            </Button>
          </Group>
        </Group>

        {/* Key Metrics Overview */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Overall Utilization
                </Text>
                <IconTrendingUp size={20} color="var(--mantine-color-blue-6)" />
              </Group>
              <Text size="xl" fw={700} c="blue">
                {totalUtilization}%
              </Text>
              <Progress
                value={totalUtilization}
                color="blue"
                size="sm"
                mt="xs"
              />
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Active Users
                </Text>
                <IconUsers size={20} color="var(--mantine-color-green-6)" />
              </Group>
              <Text size="xl" fw={700} c="green">
                {activeUsers}
              </Text>
              <Text size="xs" c="dimmed">
                of {totalUsers} total
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Active Rooms
                </Text>
                <IconBuilding size={20} color="var(--mantine-color-orange-6)" />
              </Group>
              <Text size="xl" fw={700} c="orange">
                {activeRooms}
              </Text>
              <Text size="xs" c="dimmed">
                of {totalRooms} total
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Completed Sessions
                </Text>
                <IconClock size={20} color="var(--mantine-color-purple-6)" />
              </Group>
              <Text size="xl" fw={700} c="purple">
                {completedSessions}
              </Text>
              <Text size="xs" c="dimmed">
                of {totalSessions} total
              </Text>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="utilization" leftSection={<IconTrendingUp size={16} />}>
              Utilization Reports
            </Tabs.Tab>
            <Tabs.Tab value="users" leftSection={<IconUsers size={16} />}>
              User Analytics
            </Tabs.Tab>
            <Tabs.Tab value="rooms" leftSection={<IconBuilding size={16} />}>
              Room Analytics
            </Tabs.Tab>
            <Tabs.Tab value="sessions" leftSection={<IconClock size={16} />}>
              Session Reports
            </Tabs.Tab>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="lg">
            <Grid>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Text size="lg" fw={600} mb="md">
                    System Performance Overview
                  </Text>
                  
                  <Stack gap="lg">
                    {/* Room Utilization Trend */}
                    <Paper p="lg" withBorder style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                      <Group justify="space-between" mb="md">
                        <Group gap="xs" align="center">
                          <IconBuilding size={20} color="var(--mantine-color-blue-6)" />
                          <Text size="md" fw={600} c="blue">Room Utilization Trend</Text>
                        </Group>
                        <Group gap="xs" align="center">
                          <Text size="xl" fw={700} c="blue">{totalUtilization}%</Text>
                          <Badge 
                            color={totalUtilization > 50 ? "green" : totalUtilization > 25 ? "yellow" : "red"} 
                            variant="light"
                            size="lg"
                          >
                            {totalUtilization > 50 ? "High" : totalUtilization > 25 ? "Medium" : "Low"}
                          </Badge>
                        </Group>
                      </Group>
                      <Progress
                        value={totalUtilization}
                        color={totalUtilization > 50 ? "green" : totalUtilization > 25 ? "yellow" : "red"}
                        size="xl"
                        radius="md"
                        animated={totalUtilization > 0}
                      />
                      <Group justify="space-between" mt="xs">
                        <Text size="xs" c="dimmed">
                          {analytics?.utilization?.byBuilding?.length || 0} buildings • {analytics?.utilization?.byFloor?.length || 0} floors monitored
                        </Text>
                        <Text size="xs" c="dimmed">
                          Target: 70%+ utilization
                        </Text>
                      </Group>
                    </Paper>

                    {/* User Activity */}
                    <Paper p="lg" withBorder style={{ backgroundColor: 'var(--mantine-color-green-0)' }}>
                      <Group justify="space-between" mb="md">
                        <Group gap="xs" align="center">
                          <IconUsers size={20} color="var(--mantine-color-green-6)" />
                          <Text size="md" fw={600} c="green">User Activity</Text>
                        </Group>
                        <Group gap="xs" align="center">
                          <Text size="xl" fw={700} c="green">{activeUsers}</Text>
                          <Badge 
                            color="green" 
                            variant="light"
                            size="lg"
                          >
                            {totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0}% active
                          </Badge>
                        </Group>
                      </Group>
                      <Progress
                        value={totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0}
                        color="green"
                        size="xl"
                        radius="md"
                        animated={activeUsers > 0}
                      />
                      <Group justify="space-between" mt="xs">
                        <Text size="xs" c="dimmed">
                          {activeUsers} of {totalUsers} total users active
                        </Text>
                        <Text size="xs" c="dimmed">
                          {analytics?.users?.byRole?.map(role => `${role.role}: ${role.count}`).join(' • ') || 'No role data'}
                        </Text>
                      </Group>
                    </Paper>

                    {/* Session Completion Rate */}
                    <Paper p="lg" withBorder style={{ backgroundColor: 'var(--mantine-color-purple-0)' }}>
                      <Group justify="space-between" mb="md">
                        <Group gap="xs" align="center">
                          <IconClock size={20} color="var(--mantine-color-purple-6)" />
                          <Text size="md" fw={600} c="purple">Session Completion Rate</Text>
                        </Group>
                        <Group gap="xs" align="center">
                          <Text size="xl" fw={700} c="purple">
                            {totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0}%
                          </Text>
                          <Badge 
                            color={totalSessions > 0 && (completedSessions / totalSessions) >= 0.8 ? "green" : 
                                  totalSessions > 0 && (completedSessions / totalSessions) >= 0.6 ? "yellow" : "red"} 
                            variant="light"
                            size="lg"
                          >
                            {totalSessions === 0 ? "No Sessions" : 
                             (completedSessions / totalSessions) >= 0.8 ? "Excellent" : 
                             (completedSessions / totalSessions) >= 0.6 ? "Good" : "Needs Improvement"}
                          </Badge>
                        </Group>
                      </Group>
                      <Progress
                        value={totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0}
                        color={totalSessions > 0 && (completedSessions / totalSessions) >= 0.8 ? "green" : 
                               totalSessions > 0 && (completedSessions / totalSessions) >= 0.6 ? "yellow" : "red"}
                        size="xl"
                        radius="md"
                        animated={completedSessions > 0}
                      />
                      <Group justify="space-between" mt="xs">
                        <Text size="xs" c="dimmed">
                          {completedSessions} of {totalSessions} sessions completed
                        </Text>
                        <Text size="xs" c="dimmed">
                          Target: 80%+ completion rate
                        </Text>
                      </Group>
                    </Paper>
                  </Stack>

                  {/* Detailed Breakdowns */}
                  <Stack gap="md" mt="lg">
                    {/* Utilization by Building */}
                    <Paper p="md" withBorder>
                      <Text size="sm" fw={500} mb="md">Top Buildings by Utilization</Text>
                      {analytics?.utilization?.byBuilding && analytics.utilization.byBuilding.length > 0 ? (
                        <Stack gap="xs">
                          {analytics.utilization.byBuilding.slice(0, 3).map((building) => (
                            <Group key={building.buildingId} justify="space-between" align="center">
                              <Text size="sm" fw={500}>{building.buildingName}</Text>
                              <Text size="sm" c="dimmed">{building.utilization}%</Text>
                            </Group>
                          ))}
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed">No building data available</Text>
                      )}
                    </Paper>

                    {/* Users by Role */}
                    <Paper p="md" withBorder>
                      <Text size="sm" fw={500} mb="md">User Distribution</Text>
                      {analytics?.users?.byRole && analytics.users.byRole.length > 0 ? (
                        <Stack gap="xs">
                          {analytics.users.byRole.map((roleData) => (
                            <Group key={roleData.role} justify="space-between" align="center">
                              <Badge
                                color={
                                  roleData.role === 'ADMIN' ? 'red' :
                                  roleData.role === 'COORDINATOR' ? 'blue' :
                                  roleData.role === 'DOCTOR' ? 'green' : 'gray'
                                }
                                variant="light"
                                size="sm"
                              >
                                {roleData.role}
                              </Badge>
                              <Text size="sm" fw={500}>{roleData.count}</Text>
                            </Group>
                          ))}
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed">No role data available</Text>
                      )}
                    </Paper>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Text size="lg" fw={600} mb="md">
                    Quick Actions
                  </Text>
                  
                  <Stack gap="sm">
                    <Button
                      variant="light"
                      leftSection={<IconFileText size={16} />}
                      onClick={() => handleGenerateReport('utilization')}
                      loading={analyticsLoading}
                    >
                      Generate Utilization Report
                    </Button>
                    
                    <Button
                      variant="light"
                      leftSection={<IconUsers size={16} />}
                      onClick={() => handleGenerateReport('users')}
                      loading={analyticsLoading}
                    >
                      Generate User Report
                    </Button>
                    
                    <Button
                      variant="light"
                      leftSection={<IconBuilding size={16} />}
                      onClick={() => handleGenerateReport('rooms')}
                      loading={analyticsLoading}
                    >
                      Generate Room Report
                    </Button>
                    
                    <Button
                      variant="light"
                      leftSection={<IconClock size={16} />}
                      onClick={() => handleGenerateReport('sessions')}
                      loading={analyticsLoading}
                    >
                      Generate Session Report
                    </Button>
                  </Stack>

                  {/* System Status */}
                  <Stack gap="md" mt="lg">
                    <Paper p="md" withBorder>
                      <Text size="sm" fw={500} mb="md">System Status</Text>
                      <Stack gap="xs">
                        <Group justify="space-between" align="center">
                          <Text size="sm">Active Rooms</Text>
                          <Badge color="green" variant="light" size="sm">
                            {analytics?.rooms?.active || 0} / {analytics?.rooms?.total || 0}
                          </Badge>
                        </Group>
                        <Group justify="space-between" align="center">
                          <Text size="sm">Total Sessions</Text>
                          <Badge color="blue" variant="light" size="sm">
                            {analytics?.sessions?.total || 0}
                          </Badge>
                        </Group>
                        <Group justify="space-between" align="center">
                          <Text size="sm">Last Updated</Text>
                          <Text size="xs" c="dimmed">
                            {analytics?.lastUpdated ? new Date(analytics.lastUpdated).toLocaleTimeString() : 'N/A'}
                          </Text>
                        </Group>
                      </Stack>
                    </Paper>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          {/* Utilization Reports Tab */}
          <Tabs.Panel value="utilization" pt="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Text size="lg" fw={600}>
                  Room Utilization Reports
                </Text>
                <Button
                  leftSection={<IconDownload size={16} />}
                  onClick={() => setShowExportModal(true)}
                  size="sm"
                >
                  Export
                </Button>
              </Group>

              {analyticsLoading ? (
                <Center py="xl">
                  <Stack align="center" gap="md">
                    <Loader size="md" />
                    <Text c="dimmed">Loading utilization data...</Text>
                  </Stack>
                </Center>
              ) : (
                <Stack gap="md">
                  <Text size="sm" c="dimmed">
                    Utilization reports show how effectively rooms are being used across different time periods.
                  </Text>
                  
                  {/* Overall Utilization */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">Overall Utilization</Text>
                    <Group justify="space-between" align="center">
                      <Text size="xl" fw={700} c="blue">
                        {analytics?.utilization?.overall || 0}%
                      </Text>
                      <Text size="sm" c="dimmed">
                        Last updated: {analytics?.lastUpdated ? new Date(analytics.lastUpdated).toLocaleString() : 'N/A'}
                      </Text>
                    </Group>
                  </Paper>

                  {/* Utilization by Building */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">Utilization by Building</Text>
                    {analytics?.utilization?.byBuilding && analytics.utilization.byBuilding.length > 0 ? (
                      <Stack gap="sm">
                        {analytics.utilization.byBuilding.map((building) => (
                          <Group key={building.buildingId} justify="space-between" align="center">
                            <Text size="sm" fw={500}>{building.buildingName}</Text>
                            <Group gap="xs" align="center">
                              <Text size="sm" c="dimmed">{building.utilization}%</Text>
                              <Box
                                style={{
                                  width: 60,
                                  height: 8,
                                  backgroundColor: 'var(--mantine-color-gray-2)',
                                  borderRadius: 4,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  style={{
                                    width: `${building.utilization}%`,
                                    height: '100%',
                                    backgroundColor: building.utilization > 0 ? 'var(--mantine-color-blue-5)' : 'var(--mantine-color-gray-3)',
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                              </Box>
                            </Group>
                          </Group>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">No building utilization data available</Text>
                    )}
                  </Paper>

                  {/* Utilization by Floor */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">Utilization by Floor</Text>
                    {analytics?.utilization?.byFloor && analytics.utilization.byFloor.length > 0 ? (
                      <Stack gap="sm">
                        {analytics.utilization.byFloor.map((floor) => (
                          <Group key={floor.floorId} justify="space-between" align="center">
                            <Text size="sm" fw={500}>{floor.floorName}</Text>
                            <Group gap="xs" align="center">
                              <Text size="sm" c="dimmed">{floor.utilization}%</Text>
                              <Box
                                style={{
                                  width: 60,
                                  height: 8,
                                  backgroundColor: 'var(--mantine-color-gray-2)',
                                  borderRadius: 4,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  style={{
                                    width: `${floor.utilization}%`,
                                    height: '100%',
                                    backgroundColor: floor.utilization > 0 ? 'var(--mantine-color-green-5)' : 'var(--mantine-color-gray-3)',
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                              </Box>
                            </Group>
                          </Group>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">No floor utilization data available</Text>
                    )}
                  </Paper>
                </Stack>
              )}
            </Card>
          </Tabs.Panel>

          {/* User Analytics Tab */}
          <Tabs.Panel value="users" pt="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text size="lg" fw={600} mb="md">
                User Analytics
              </Text>
              
              <Text size="sm" c="dimmed" mb="md">
                User activity analytics and engagement metrics.
              </Text>
              
              {analyticsLoading ? (
                <Center py="xl">
                  <Stack align="center" gap="md">
                    <Loader size="md" />
                    <Text c="dimmed">Loading user analytics...</Text>
                  </Stack>
                </Center>
              ) : (
                <Stack gap="md">
                  {/* User Summary */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">User Summary</Text>
                    <Group justify="space-around" align="center">
                      <Stack align="center" gap="xs">
                        <Text size="xl" fw={700} c="green">
                          {analytics?.users?.active || 0}
                        </Text>
                        <Text size="sm" c="dimmed">Active Users</Text>
                      </Stack>
                      <Stack align="center" gap="xs">
                        <Text size="xl" fw={700} c="blue">
                          {analytics?.users?.total || 0}
                        </Text>
                        <Text size="sm" c="dimmed">Total Users</Text>
                      </Stack>
                    </Group>
                  </Paper>

                  {/* Users by Role */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">Users by Role</Text>
                    {analytics?.users?.byRole && analytics.users.byRole.length > 0 ? (
                      <Stack gap="sm">
                        {analytics.users.byRole.map((roleData) => (
                          <Group key={roleData.role} justify="space-between" align="center">
                            <Group gap="xs" align="center">
                              <Badge
                                color={
                                  roleData.role === 'ADMIN' ? 'red' :
                                  roleData.role === 'COORDINATOR' ? 'blue' :
                                  roleData.role === 'DOCTOR' ? 'green' : 'gray'
                                }
                                variant="light"
                                size="sm"
                              >
                                {roleData.role}
                              </Badge>
                              <Text size="sm" fw={500}>
                                {roleData.count} {roleData.count === 1 ? 'user' : 'users'}
                              </Text>
                            </Group>
                            <Group gap="xs" align="center">
                              <Text size="sm" c="dimmed">
                                {((roleData.count / (analytics?.users?.total || 1)) * 100).toFixed(1)}%
                              </Text>
                              <Box
                                style={{
                                  width: 60,
                                  height: 8,
                                  backgroundColor: 'var(--mantine-color-gray-2)',
                                  borderRadius: 4,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  style={{
                                    width: `${(roleData.count / (analytics?.users?.total || 1)) * 100}%`,
                                    height: '100%',
                                    backgroundColor: 
                                      roleData.role === 'ADMIN' ? 'var(--mantine-color-red-5)' :
                                      roleData.role === 'COORDINATOR' ? 'var(--mantine-color-blue-5)' :
                                      roleData.role === 'DOCTOR' ? 'var(--mantine-color-green-5)' : 'var(--mantine-color-gray-5)',
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                              </Box>
                            </Group>
                          </Group>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">No role distribution data available</Text>
                    )}
                  </Paper>

                  {/* Last Updated */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="sm">Data Last Updated</Text>
                    <Text size="sm" c="dimmed">
                      {analytics?.lastUpdated ? new Date(analytics.lastUpdated).toLocaleString() : 'N/A'}
                    </Text>
                  </Paper>
                </Stack>
              )}
            </Card>
          </Tabs.Panel>

          {/* Room Analytics Tab */}
          <Tabs.Panel value="rooms" pt="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text size="lg" fw={600} mb="md">
                Room Analytics
              </Text>
              
              <Text size="sm" c="dimmed" mb="md">
                Room usage patterns, capacity analysis, and efficiency metrics.
              </Text>
              
              {analyticsLoading ? (
                <Center py="xl">
                  <Stack align="center" gap="md">
                    <Loader size="md" />
                    <Text c="dimmed">Loading room analytics...</Text>
                  </Stack>
                </Center>
              ) : (
                <Stack gap="md">
                  {/* Room Summary */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">Room Summary</Text>
                    <Group justify="space-around" align="center">
                      <Stack align="center" gap="xs">
                        <Text size="xl" fw={700} c="orange">
                          {analytics?.rooms?.active || 0}
                        </Text>
                        <Text size="sm" c="dimmed">Active Rooms</Text>
                      </Stack>
                      <Stack align="center" gap="xs">
                        <Text size="xl" fw={700} c="blue">
                          {analytics?.rooms?.total || 0}
                        </Text>
                        <Text size="sm" c="dimmed">Total Rooms</Text>
                      </Stack>
                    </Group>
                  </Paper>

                  {/* Rooms by Status */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">Rooms by Status</Text>
                    {analytics?.rooms?.byStatus && analytics.rooms.byStatus.length > 0 ? (
                      <Stack gap="sm">
                        {analytics.rooms.byStatus.map((statusData) => (
                          <Group key={statusData.status} justify="space-between" align="center">
                            <Group gap="xs" align="center">
                              <Badge
                                color={
                                  statusData.status === 'ACTIVE' ? 'green' :
                                  statusData.status === 'INACTIVE' ? 'red' :
                                  statusData.status === 'MAINTENANCE' ? 'yellow' : 'gray'
                                }
                                variant="light"
                                size="sm"
                              >
                                {statusData.status}
                              </Badge>
                              <Text size="sm" fw={500}>
                                {statusData.count} {statusData.count === 1 ? 'room' : 'rooms'}
                              </Text>
                            </Group>
                            <Group gap="xs" align="center">
                              <Text size="sm" c="dimmed">
                                {((statusData.count / (analytics?.rooms?.total || 1)) * 100).toFixed(1)}%
                              </Text>
                              <Box
                                style={{
                                  width: 60,
                                  height: 8,
                                  backgroundColor: 'var(--mantine-color-gray-2)',
                                  borderRadius: 4,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  style={{
                                    width: `${(statusData.count / (analytics?.rooms?.total || 1)) * 100}%`,
                                    height: '100%',
                                    backgroundColor: 
                                      statusData.status === 'ACTIVE' ? 'var(--mantine-color-green-5)' :
                                      statusData.status === 'INACTIVE' ? 'var(--mantine-color-red-5)' :
                                      statusData.status === 'MAINTENANCE' ? 'var(--mantine-color-yellow-5)' : 'var(--mantine-color-gray-5)',
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                              </Box>
                            </Group>
                          </Group>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">No room status data available</Text>
                    )}
                  </Paper>

                  {/* Last Updated */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="sm">Data Last Updated</Text>
                    <Text size="sm" c="dimmed">
                      {analytics?.lastUpdated ? new Date(analytics.lastUpdated).toLocaleString() : 'N/A'}
                    </Text>
                  </Paper>
                </Stack>
              )}
            </Card>
          </Tabs.Panel>

          {/* Session Reports Tab */}
          <Tabs.Panel value="sessions" pt="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text size="lg" fw={600} mb="md">
                Session Reports
              </Text>
              
              <Text size="sm" c="dimmed" mb="md">
                Session scheduling, completion rates, and conflict analysis.
              </Text>
              
              {analyticsLoading ? (
                <Center py="xl">
                  <Stack align="center" gap="md">
                    <Loader size="md" />
                    <Text c="dimmed">Loading session reports...</Text>
                  </Stack>
                </Center>
              ) : (
                <Stack gap="md">
                  {/* Session Summary */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">Session Summary</Text>
                    <Group justify="space-around" align="center">
                      <Stack align="center" gap="xs">
                        <Text size="xl" fw={700} c="purple">
                          {analytics?.sessions?.completed || 0}
                        </Text>
                        <Text size="sm" c="dimmed">Completed Sessions</Text>
                      </Stack>
                      <Stack align="center" gap="xs">
                        <Text size="xl" fw={700} c="blue">
                          {analytics?.sessions?.total || 0}
                        </Text>
                        <Text size="sm" c="dimmed">Total Sessions</Text>
                      </Stack>
                    </Group>
                  </Paper>

                  {/* Completion Rate */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">Completion Rate</Text>
                    <Group justify="space-between" align="center">
                      <Text size="xl" fw={700} c="green">
                        {(analytics?.sessions?.total ?? 0) > 0 
                          ? (((analytics?.sessions?.completed ?? 0) / (analytics?.sessions?.total ?? 1)) * 100).toFixed(1)
                          : 0}%
                      </Text>
                      <Box
                        style={{
                          width: 120,
                          height: 12,
                          backgroundColor: 'var(--mantine-color-gray-2)',
                          borderRadius: 6,
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          style={{
                            width: `${(analytics?.sessions?.total ?? 0) > 0 
                              ? ((analytics?.sessions?.completed ?? 0) / (analytics?.sessions?.total ?? 1)) * 100 
                              : 0}%`,
                            height: '100%',
                            backgroundColor: (analytics?.sessions?.total ?? 0) > 0 
                              ? 'var(--mantine-color-green-5)' 
                              : 'var(--mantine-color-gray-3)',
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </Box>
                    </Group>
                  </Paper>

                  {/* Sessions by Type */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="md">Sessions by Type</Text>
                    {analytics?.sessions?.byType && analytics.sessions.byType.length > 0 ? (
                      <Stack gap="sm">
                        {analytics.sessions.byType.map((typeData) => (
                          <Group key={typeData.type} justify="space-between" align="center">
                            <Group gap="xs" align="center">
                              <Badge
                                color="blue"
                                variant="light"
                                size="sm"
                              >
                                {typeData.type}
                              </Badge>
                              <Text size="sm" fw={500}>
                                {typeData.count} {typeData.count === 1 ? 'session' : 'sessions'}
                              </Text>
                            </Group>
                            <Group gap="xs" align="center">
                              <Text size="sm" c="dimmed">
                                {(analytics?.sessions?.total ?? 0) > 0 
                                  ? ((typeData.count / (analytics?.sessions?.total ?? 1)) * 100).toFixed(1)
                                  : 0}%
                              </Text>
                              <Box
                                style={{
                                  width: 60,
                                  height: 8,
                                  backgroundColor: 'var(--mantine-color-gray-2)',
                                  borderRadius: 4,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  style={{
                                    width: `${(analytics?.sessions?.total ?? 0) > 0 
                                      ? (typeData.count / (analytics?.sessions?.total ?? 1)) * 100 
                                      : 0}%`,
                                    height: '100%',
                                    backgroundColor: 'var(--mantine-color-blue-5)',
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                              </Box>
                            </Group>
                          </Group>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">No session type data available</Text>
                    )}
                  </Paper>

                  {/* Last Updated */}
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="sm">Data Last Updated</Text>
                    <Text size="sm" c="dimmed">
                      {analytics?.lastUpdated ? new Date(analytics.lastUpdated).toLocaleString() : 'N/A'}
                    </Text>
                  </Paper>
                </Stack>
              )}
            </Card>
          </Tabs.Panel>
        </Tabs>

        {/* Export Modal */}
        <Modal
          opened={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Export Report"
          size="md"
        >
          <Stack gap="md">
            <Select
              label="Report Type"
              data={[
                { value: 'utilization', label: 'Utilization Report' },
                { value: 'users', label: 'User Analytics' },
                { value: 'rooms', label: 'Room Analytics' },
                { value: 'sessions', label: 'Session Reports' },
              ]}
              value={selectedReport}
              onChange={(value) => setSelectedReport(value || 'utilization')}
            />
            
            <Stack gap="md">
              <Button
                variant="outline"
                leftSection={<IconFileTypePdf size={18} />}
                onClick={() => handleExportReport('pdf')}
                fullWidth
                size="md"
                color="red"
                style={{
                  borderColor: 'var(--mantine-color-red-4)',
                  color: 'var(--mantine-color-red-6)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--mantine-color-red-0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="outline"
                leftSection={<IconFileSpreadsheet size={18} />}
                onClick={() => handleExportReport('excel')}
                fullWidth
                size="md"
                color="green"
                style={{
                  borderColor: 'var(--mantine-color-green-4)',
                  color: 'var(--mantine-color-green-6)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--mantine-color-green-0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Export Excel
              </Button>
              <Button
                variant="outline"
                leftSection={<IconFileTypeCsv size={18} />}
                onClick={() => handleExportReport('csv')}
                fullWidth
                size="md"
                color="blue"
                style={{
                  borderColor: 'var(--mantine-color-blue-4)',
                  color: 'var(--mantine-color-blue-6)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--mantine-color-blue-0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Export CSV
              </Button>
            </Stack>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
}
